import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

/** Props for the IndexSection component. */
interface IndexSectionProps {
  title: string;
  count: number;
  children: ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  contentClassName?: string;
}

/**
 * A labelled index group with its item count set as an editorial section
 * header. Renders nothing when empty.
 * @param props - The component props
 * @returns The index section, or null when there are no items
 */
export function IndexSection({
  title,
  count,
  children,
  collapsible = false,
  defaultOpen = true,
  contentClassName,
}: IndexSectionProps) {
  if (count === 0) {
    return null;
  }

  if (collapsible) {
    return (
      <Collapsible
        defaultOpen={defaultOpen}
        className="border-t border-border/60 py-1.5 first:border-t-0"
      >
        <CollapsibleTrigger className="group flex w-full items-center justify-between px-4 py-1 text-left transition-colors hover:text-foreground">
          <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            {title}
          </span>
          <span className="flex items-center gap-1.5 text-[10px] tabular-nums text-muted-foreground/50">
            {count}
            <ChevronDown className="h-3 w-3 transition-transform duration-200 group-data-[state=open]:rotate-180" />
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
          <div className={cn("pt-1", contentClassName)}>{children}</div>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <div className="border-t border-border/60 pb-1.5 pt-2.5 first:border-t-0">
      <div className="flex items-baseline justify-between px-4 pb-1">
        <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          {title}
        </span>
        <span className="text-[10px] tabular-nums text-muted-foreground/50">
          {count}
        </span>
      </div>
      {children}
    </div>
  );
}
